import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { Loader2 } from "lucide-react";
import "@/App.css";

import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LiveCoachProvider } from "@/context/LiveCoachContext";
import DeveloperMonitor from "@/components/DeveloperMonitor";
import { AppShell } from "@/components/layout/AppShell";
import { isStaff, hasCap } from "@/lib/roles";

import Login from "@/pages/auth/Login";
import Register from "@/pages/auth/Register";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import ResetPassword from "@/pages/auth/ResetPassword";
import VerifyEmail from "@/pages/auth/VerifyEmail";
import ChooseDestination from "@/pages/ChooseDestination";
import Onboarding from "@/pages/Onboarding";
import ShareClip from "@/pages/ShareClip";
import { AdminLayout } from "@/components/layout/AdminLayout";
import AdminOverview from "@/pages/admin/AdminOverview";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminBusiness from "@/pages/admin/AdminBusiness";
import AdminTeams from "@/pages/admin/AdminTeams";
import AdminStaff from "@/pages/admin/AdminStaff";
import AdminDeveloperMonitor from "@/pages/admin/AdminDeveloperMonitor";

import Dashboard from "@/pages/Dashboard";
import Matches from "@/pages/Matches";
import Coach from "@/pages/Coach";
import Improvement from "@/pages/Improvement";
import Career from "@/pages/Career";
import RoadToPro from "@/pages/RoadToPro";
import Scout from "@/pages/Scout";
import Clips from "@/pages/Clips";
import Content from "@/pages/Content";
import Storage from "@/pages/Storage";
import Integrity from "@/pages/Integrity";
import Evidence from "@/pages/Evidence";
import Analytics from "@/pages/Analytics";
import Achievements from "@/pages/Achievements";
import Settings from "@/pages/Settings";
import Profile from "@/pages/Profile";
import Pricing from "@/pages/Pricing";
import BillingSuccess from "@/pages/BillingSuccess";
import Landing from "@/pages/Landing";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";

function FullLoader() {
    return (
        <div className="min-h-screen grid place-items-center bg-obsidian">
            <Loader2 className="w-7 h-7 animate-spin text-volt" />
        </div>
    );
}

function ProtectedLayout() {
    const { user } = useAuth();
    if (user === null) return <FullLoader />;
    if (user === false) return <Navigate to="/login" replace />;
    return (
        <LiveCoachProvider>
            <AppShell />
        </LiveCoachProvider>
    );
}

function PublicOnly({ children }) {
    const { user } = useAuth();
    if (user === null) return <FullLoader />;
    if (user) {
        if (!isStaff(user) && sessionStorage.getItem("ww_onboard")) {
            return <Navigate to="/onboarding" replace />;
        }
        return <Navigate to={isStaff(user) ? "/choose" : "/dashboard"} replace />;
    }
    return children;
}

function RootRedirect() {
    const { user } = useAuth();
    if (user === null) return <FullLoader />;
    if (!user) return <Navigate to="/" replace />;
    return <Navigate to={isStaff(user) ? "/choose" : "/dashboard"} replace />;
}

// Public marketing landing page at "/"; authenticated users go straight to their app.
function LandingRoute() {
    const { user } = useAuth();
    if (user === null) return <FullLoader />;
    if (user) return <Navigate to={isStaff(user) ? "/choose" : "/dashboard"} replace />;
    return <Landing />;
}

function AdminProtectedLayout() {
    const { user } = useAuth();
    if (user === null) return <FullLoader />;
    if (user === false) return <Navigate to="/login" replace />;
    if (!isStaff(user)) return <Navigate to="/dashboard" replace />;
    return <AdminLayout />;
}

// Route guard requiring a specific capability; redirects staff lacking it to the portal overview.
function RequireCap({ cap, children }) {
    const { user } = useAuth();
    if (user === null) return <FullLoader />;
    if (user === false) return <Navigate to="/login" replace />;
    if (!hasCap(user, cap)) return <Navigate to="/admin" replace />;
    return children;
}

function ChooseGuard() {
    const { user } = useAuth();
    if (user === null) return <FullLoader />;
    if (user === false) return <Navigate to="/login" replace />;
    return <ChooseDestination />;
}

function OnboardingGuard() {
    const { user } = useAuth();
    if (user === null) return <FullLoader />;
    if (user === false) return <Navigate to="/login" replace />;
    return <Onboarding />;
}

function AppRoutes() {
    return (
        <Routes>
            <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
            <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
            <Route path="/forgot-password" element={<PublicOnly><ForgotPassword /></PublicOnly>} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/choose" element={<ChooseGuard />} />
            <Route path="/onboarding" element={<OnboardingGuard />} />
            <Route path="/s/clip/:token" element={<ShareClip />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />

            <Route element={<AdminProtectedLayout />}>
                <Route path="/admin" element={<AdminOverview />} />
                <Route path="/admin/business" element={<RequireCap cap="business"><AdminBusiness /></RequireCap>} />
                <Route path="/admin/teams" element={<RequireCap cap="teams"><AdminTeams /></RequireCap>} />
                <Route path="/admin/staff" element={<RequireCap cap="staff"><AdminStaff /></RequireCap>} />
                <Route path="/admin/users" element={<RequireCap cap="users"><AdminUsers /></RequireCap>} />
                <Route path="/admin/developer-monitor" element={<RequireCap cap="config"><AdminDeveloperMonitor /></RequireCap>} />
            </Route>

            <Route element={<ProtectedLayout />}>
                <Route path="/dashboard" element={<Dashboard />} />
                {/* LiveCapture stays mounted inside AppShell so active capture survives navigation. */}
                <Route path="/live" element={null} />
                <Route path="/matches" element={<Matches />} />
                <Route path="/coach" element={<Coach />} />
                <Route path="/improvement" element={<Improvement />} />
                <Route path="/career" element={<Career />} />
                <Route path="/road-to-pro" element={<RoadToPro />} />
                <Route path="/scout" element={<Scout />} />
                <Route path="/clips" element={<Clips />} />
                <Route path="/content" element={<Content />} />
                <Route path="/storage" element={<Storage />} />
                <Route path="/integrity" element={<Integrity />} />
                <Route path="/evidence" element={<Evidence />} />
                <Route path="/analytics" element={<Analytics />} />
                <Route path="/achievements" element={<Achievements />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/billing/success" element={<BillingSuccess />} />
            </Route>

            <Route path="/" element={<LandingRoute />} />
            <Route path="*" element={<RootRedirect />} />
        </Routes>
    );
}

export default function App() {
    useEffect(() => {
        document.documentElement.classList.add("dark");
    }, []);

    return (
        <div className="App">
            <BrowserRouter>
                <AuthProvider>
                    <DeveloperMonitor />
                    <AppRoutes />
                    <Toaster theme="dark" position="top-right" richColors closeButton
                        toastOptions={{ style: { background: "#1A1A1A", border: "1px solid #2a2a2a", color: "#fff" } }} />
                </AuthProvider>
            </BrowserRouter>
        </div>
    );
}
